"use client";

import { Button } from "../button";
import { HighlightPrice } from "../highlight-price";
import { getCurrencySymbol, formatPrice } from "./utils";

export type BookingOptionProps = {
  /** Option title (e.g., "Full Term", "Trial", "Drop-in") */
  title: string;
  /** Description text */
  description: string;
  /** Price in minor units (cents/pence) */
  price?: number;
  /** Whether to show "from" prefix for price */
  isFromPrice?: boolean;
  /** Currency code (e.g., "GBP") */
  currency?: string;
  /** Discount information */
  discount?: {
    value: number;
    type: string;
  };
  /** Discounted price in minor units (pre-calculated) */
  discountedPrice?: number;
  /** Button text */
  buttonText?: string;
  /** Click handler */
  onSelect: () => void;
};

/**
 * Individual booking option card component.
 * Displays a booking option with title, description, price, and select button.
 */
export function BookingOption({
  title,
  description,
  price,
  isFromPrice = false,
  currency = "GBP",
  discount,
  discountedPrice,
  buttonText = "Select",
  onSelect,
}: BookingOptionProps) {
  const currencySymbol = getCurrencySymbol(currency);

  // Format price for display
  const formatDisplayPrice = (priceValue: number): string => {
    const value = priceValue / 100;
    if (value % 1 === 0) {
      return `${currencySymbol}${value}`;
    }
    return `${currencySymbol}${value.toFixed(2)}`;
  };

  return (
    <div className="bg-card rounded-lg p-4 h-full border border-border shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col gap-3 sm:gap-4">
        {/* Title and description */}
        <div className="flex flex-col gap-1">
          <span className="text-foreground font-medium text-base leading-tight">
            {title}
          </span>
          <span className="text-muted-foreground leading-snug text-sm">
            {description}
          </span>
        </div>

        {/* Price */}
        {price !== undefined && (
          <div className="flex items-center gap-2">
            {discount && discountedPrice !== undefined && (
              <HighlightPrice
                price={discountedPrice}
                currency={currency}
                size="md"
              />
            )}
            <span
              className={`font-semibold leading-6 tracking-wide text-lg ${
                discount !== undefined
                  ? "line-through text-muted-foreground/70"
                  : "text-foreground"
              }`}
            >
              {price > 0 && formatDisplayPrice(price)}
              {price === 0 && "Free"}
            </span>
          </div>
        )}

        {/* Button */}
        <Button
          onClick={onSelect}
          variant="default"
          size="default"
          className="w-full uppercase font-semibold tracking-wide bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
}
